import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { add, format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";

const Schedule: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [startTimeInput, setStartTimeInput] = useState("");
  const [endTimeInput, setEndTimeInput] = useState("");

  const startHour = watch("start_window_begin_hour");
  const startMinute = watch("start_window_begin_minute");
  const startMeridiem = watch("start_window_begin_meridiem");
  const endHour = watch("start_window_end_hour");
  const endMinute = watch("start_window_end_minute");
  const endMeridiem = watch("start_window_end_meridiem");

  const formatTime = (
    hour: number | null,
    minute: number | null,
    meridiem: string | null
  ) => {
    if (hour === null || minute === null || meridiem === null) {
      return "";
    }

    // Convert 24h to 12h format
    let displayHour = hour;
    if (hour > 12) {
      displayHour = hour - 12;
    } else if (hour === 0) {
      displayHour = 12;
    }

    return `${displayHour}:${minute.toString().padStart(2, "0")}${meridiem}`;
  };

  const parseTime = (timeString: string) => {
    const match = timeString.match(/^(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?$/i);
    if (match) {
      const [, hour, minute, meridiem] = match;
      let parsedHour = parseInt(hour, 10);
      const parsedMinute = minute ? parseInt(minute, 10) : 0;
      const parsedMeridiem = meridiem
        ? meridiem.toUpperCase()
        : parsedHour < 12
        ? "AM"
        : "PM";

      // Keep hours in 12-hour format
      if (parsedHour > 12) {
        parsedHour = parsedHour - 12;
      } else if (parsedHour === 0) {
        parsedHour = 12;
      }

      return {
        hour: parsedHour,
        minute: parsedMinute,
        meridiem: parsedMeridiem,
      };
    }
    return {
      hour: null,
      minute: null,
      meridiem: null,
    };
  };

  useEffect(() => {
    if (startHour !== null && startMinute !== null && startMeridiem !== null) {
      setStartTimeInput(formatTime(startHour, startMinute, startMeridiem));
    }
  }, [startHour, startMinute, startMeridiem]);

  useEffect(() => {
    if (endHour !== null && endMinute !== null && endMeridiem !== null) {
      setEndTimeInput(formatTime(endHour, endMinute, endMeridiem));
    }
  }, [endHour, endMinute, endMeridiem]);

  const handleTimeChange = (timeString: string, isStartTime: boolean) => {
    if (isStartTime) {
      setStartTimeInput(timeString);
    } else {
      setEndTimeInput(timeString);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row gap-4">
        <FormField
          control={control}
          name="service_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="flex flex-row items-center gap-2">
                Date
                <FormMessage />
              </FormLabel>
              <div className="flex flex-row items-center gap-4">
                <Popover
                  open={datePopoverOpen}
                  onOpenChange={setDatePopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-[130px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(add(field.value, { hours: 12 }), "MM/dd/yyyy")
                        ) : (
                          <span>Date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        typeof field.value === "string"
                          ? new Date(field.value + "T12:00:00")
                          : field.value
                      }
                      onSelect={(date) => {
                        field.onChange(
                          date ? format(date, "yyyy-MM-dd") : date
                        );
                        setDatePopoverOpen(false);
                      }}
                      disabled={(date) => {
                        const yesterday = new Date(
                          new Date().setDate(new Date().getDate() - 1)
                        );
                        return date < yesterday;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </FormItem>
          )}
        />

        <div className="">
          <FormLabel className="flex flex-row items-center gap-2">
            Arrival Window
            {(errors.start_window_begin || errors.start_window_end) && (
              <FormMessage>
                {" "}
                {errors.start_window_begin?.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <div className="flex flex-row items-center gap-2">
            <FormField
              control={control}
              name="start_window_begin"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      value={startTimeInput}
                      onChange={(e) => handleTimeChange(e.target.value, true)}
                      onBlur={() => {
                        const parsed = parseTime(startTimeInput);
                        if (parsed) {
                          setValue("start_window_begin_hour", parsed.hour);
                          setValue("start_window_begin_minute", parsed.minute);
                          setValue(
                            "start_window_begin_meridiem",
                            parsed.meridiem
                          );
                          setStartTimeInput(
                            formatTime(
                              parsed.hour,
                              parsed.minute,
                              parsed.meridiem
                            )
                          );
                        }
                      }}
                      className="w-20"
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <span>to</span>
            <FormField
              control={control}
              name="start_window_end"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      value={endTimeInput}
                      onChange={(e) => handleTimeChange(e.target.value, false)}
                      onBlur={() => {
                        const parsed = parseTime(endTimeInput);
                        if (parsed) {
                          setValue("start_window_end_hour", parsed.hour);
                          setValue("start_window_end_minute", parsed.minute);
                          setValue(
                            "start_window_end_meridiem",
                            parsed.meridiem
                          );
                          setEndTimeInput(
                            formatTime(
                              parsed.hour,
                              parsed.minute,
                              parsed.meridiem
                            )
                          );
                        }
                      }}
                      className="w-20"
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <span className="font-normal text-xs">(eg 8am, 130pm)</span>
        </div>

        <div>
          <FormLabel className="flex flex-row items-center gap-2">
            Estimated Hours
            {errors.hours_estimate && (
              <FormMessage>
                {" "}
                {errors.hours_estimate.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <FormField
            control={control}
            name="hours_estimate"
            render={({ field }) => (
              <FormItem className="flex flex-row items-baseline">
                <FormControl>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-r-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        if (currentValue > 0) {
                          field.onChange(currentValue - 1);
                        }
                      }}
                    >
                      -
                    </Button>
                    <Input
                      className="w-12 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "") field.onChange(null);
                        else if (!isNaN(parseInt(e.target.value))) {
                          field.onChange(parseInt(e.target.value));
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-l-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        field.onChange(currentValue + 1);
                      }}
                    >
                      +
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Schedule;
